import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  useDiagnosticReceipts,
  useDiagnosticReceipt,
  useTrashDiagnosticReceipts,
  useRecordDiagnosticReceiptPayment,
  useMoveToRiskFund,
} from '../../services/httpServices/queries';
import { usePermissions } from '../../hooks/usePermissions';
import { useDebounce } from '../../hooks/useDebounce';
import { diagnosticService } from '../../services/httpServices/diagnosticService';
import { getDiagnosticReceiptPrinter } from '../../utils/devices';
import { printPdfToDevice } from '../../utils/printDocument';
import { PrinterTabs } from '../../components/ui/PrinterTabs';
import { usePrinterTab } from '../../hooks/usePrinterTab';
import { Pagination } from '../../components/ui/Pagination';
import { joinFullName } from '../../utils/patientName';
import {
  DIAGNOSTIC_PAYMENT_STATUS_META,
  DIAGNOSTIC_PAYMENT_STATUS_OPTIONS as PAYMENT_STATUSES,
  DIAGNOSTIC_RECEIPT_STATUS_OPTIONS as STATUSES,
  diagnosticPaymentStatusFilter,
  diagnosticPaymentStatusOf as paymentStatusOf,
  diagnosticReceiptStatusClass,
  diagnosticReceiptStatusLabel,
  type DiagnosticPaymentStatus as PaymentStatus,
} from '../../utils/diagnosticReceiptStatus';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { RiskFundMoveDialog } from '../../components/ui/RiskFundMoveDialog';
import { ValidatedNumberInput } from '../../components/ui/validated-number-input';
import { DatePicker } from '../../components/ui/DatePicker';
import { useHospitalTimezone } from '../../hooks/useHospitalTimezone';
import {
  DEFAULT_HOSPITAL_TIMEZONE,
  datetimeLocalToISOInTimeZone,
  toDatetimeLocalValueInTimeZone,
} from '../../lib/timezones';
import { ReferralCalculationModal } from '../../components/ui/ReferralCalculationModal';
import {
  ClipboardList,
  Loader2,
  AlertCircle,
  AlertTriangle,
  Plus,
  Printer,
  Download,
  Stethoscope,
  UserPlus,
  CheckCircle2,
  ExternalLink,
  Search,
  X,
  Trash2,
  SlidersHorizontal,
  Pencil,
  User,
  Calendar,
  FlaskConical,
  Banknote,
  Info,
} from 'lucide-react';

/**
 * Splits a whole-taka referral total across test lines so the calculation
 * breakdown's lines add up exactly to the rounded payout (largest remainder).
 */
function allocateWholeTaka(total: number, weights: number[]): number[] {
  const target = Math.max(0, Math.round(Number(total) || 0));
  const result = weights.map(() => 0);
  const totalWeight = weights.reduce(
    (sum, weight) => sum + Math.max(0, Number(weight) || 0),
    0,
  );
  if (target === 0 || weights.length === 0 || totalWeight <= 0) return result;

  const fractions: Array<{ index: number; fraction: number }> = [];
  let assigned = 0;
  weights.forEach((weight, index) => {
    const ideal = (target * Math.max(0, Number(weight) || 0)) / totalWeight;
    const whole = Math.floor(ideal + 1e-9);
    result[index] = whole;
    assigned += whole;
    fractions.push({ index, fraction: ideal - whole });
  });

  let remainder = target - assigned;
  fractions.sort((a, b) => b.fraction - a.fraction);
  while (remainder > 0) {
    for (const { index } of fractions) {
      if (remainder === 0) break;
      result[index] += 1;
      remainder -= 1;
    }
  }
  return result;
}

const PAYMENT_MODES = [
  { value: 'cash', label: 'Cash' },
  { value: 'card', label: 'Card' },
  { value: 'upi', label: 'UPI' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'insurance', label: 'Insurance' },
];

function PaymentStatusBadge({ status }: { status: PaymentStatus }) {
  const meta = DIAGNOSTIC_PAYMENT_STATUS_META[status];
  return (
    <span
      className={`inline-flex px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${meta.className}`}
      title={`Payment ${meta.label}`}
    >
      {meta.label}
    </span>
  );
}

type TrashConfirm =
  | { mode: 'single'; receiptId: string; receiptNumber: string }
  | { mode: 'bulk' }
  | null;

type RiskFundConfirm =
  | { mode: 'single'; receiptId: string; receiptNumber: string; dueAmount: number }
  | { mode: 'bulk' }
  | null;

export function DiagnosticReceipts() {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission } = usePermissions();
  const limit = 20;
  const trashMutation = useTrashDiagnosticReceipts();
  const recordPaymentMutation = useRecordDiagnosticReceiptPayment();
  const moveToRiskFund = useMoveToRiskFund();
  const canDelete = hasPermission('diagnostics:receipt:delete');
  const canUpdate = hasPermission('diagnostics:receipt:update');
  // Writing off a receivable is restricted to admins and custom roles that were
  // explicitly granted the capability.
  const canManageRiskFund = hasPermission('billing:risk-fund:manage');
  // Row selection drives the bulk trash *and* the bulk write-off, so it is
  // available to either capability rather than only the delete permission.
  const canSelectReceipts = canDelete || canManageRiskFund;

  // Hospital timezone (Settings → General → `hospital_timezone`). Payment dates
  // are wall-clock times in this zone, not the browser's; read from the public
  // settings endpoint so every role resolves the hospital's calendar.
  const { timezone: hospitalTimezone } = useHospitalTimezone();

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const [page, setPage] = useState(() => {
    const p = params.get('page');
    return p ? Math.max(1, parseInt(p, 10)) : 1;
  });
  const [search, setSearch] = useState(() => params.get('search') || '');
  const debouncedSearch = useDebounce(search, 350);
  const [status, setStatus] = useState(() => params.get('status') || '');
  const [paymentMode, setPaymentMode] = useState(() => params.get('paymentMode') || '');
  const [paymentStatus, setPaymentStatus] = useState(() =>
    diagnosticPaymentStatusFilter(params.get('paymentStatus')),
  );
  // '' = all receipts, 'true' = referral receipts, 'false' = direct/walk-in.
  const [isReferral, setIsReferral] = useState<'true' | 'false' | ''>(() => {
    const value = params.get('isReferral');
    return value === 'true' || value === 'false' ? value : '';
  });
  const [dateFrom, setDateFrom] = useState(() => params.get('dateFrom') || '');
  const [dateTo, setDateTo] = useState(() => params.get('dateTo') || '');
  // Which date the range applies to: the receipt was created (default) or its
  // balance was settled ("Settled On" column). Same semantics as the backend.
  const [dateBasis, setDateBasis] = useState<'document' | 'settled'>(() =>
    params.get('dateBasis') === 'settled' ? 'settled' : 'document',
  );
  const [showFilters, setShowFilters] = useState(false);

  const [printingId, setPrintingId] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [printError, setPrintError] = useState<string | null>(null);
  const [justCreated, setJustCreated] = useState(() => {
    return !!(location.state as any)?.justCreated;
  });

  // Receipt detail modal opened by clicking a receipt number.
  const [detailReceipt, setDetailReceipt] = useState<any | null>(null);
  // Which tab is active in the receipt details modal.
  const [detailTab, setDetailTab] = useState<'tests' | 'payments'>('tests');
  // "How is this calculated?" dialog for the referral commission, opened from
  // the details modal the same way the edit page opens it.
  const [showDetailReferralInfo, setShowDetailReferralInfo] = useState(false);

  // Due payment modal — opened from the receipt details modal to collect an
  // outstanding balance against the receipt.
  const [duePaymentReceipt, setDuePaymentReceipt] = useState<any | null>(null);
  const [duePaymentAmount, setDuePaymentAmount] = useState<number>(0);
  const [duePaymentAmountDirty, setDuePaymentAmountDirty] = useState<boolean>(false);
  const [duePaymentMode, setDuePaymentMode] = useState<string>('cash');
  const [duePaymentDiscountType, setDuePaymentDiscountType] = useState<'FLAT' | 'PERCENT'>('FLAT');
  const [duePaymentDiscountValue, setDuePaymentDiscountValue] = useState<number>(0);
  const [duePaymentReference, setDuePaymentReference] = useState<string>('');
  // The actual date the money was received. Sent with the payment so the
  // collected amount posts on that day, even when it is later than the receipt.
  // Interpreted as a wall-clock time in the hospital's zone (Settings → General).
  const [duePaymentDate, setDuePaymentDate] = useState<string>(() =>
    toDatetimeLocalValueInTimeZone(new Date(), DEFAULT_HOSPITAL_TIMEZONE),
  );
  // Optional promised date for whatever balance remains after this payment.
  const [duePaymentDueDate, setDuePaymentDueDate] = useState<string>('');
  const [duePaymentError, setDuePaymentError] = useState<string | null>(null);

  // Row selection for bulk actions
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const headerCheckboxRef = useRef<HTMLInputElement>(null);
  const [trashConfirm, setTrashConfirm] = useState<TrashConfirm>(null);
  const [trashError, setTrashError] = useState<string | null>(null);

  const [riskFundConfirm, setRiskFundConfirm] = useState<RiskFundConfirm>(null);
  const [riskFundReason, setRiskFundReason] = useState('');
  const [riskFundAmount, setRiskFundAmount] = useState('');
  const [riskFundError, setRiskFundError] = useState<string | null>(null);

  if ((location.state as any)?.justCreated) {
    navigate(location.pathname, { replace: true, state: {} });
    setTimeout(() => setJustCreated(false), 3000);
  }

  // Print target selected via the ?printer= tab strip. Defaults to the printer
  // designated for diagnostic receipts (Settings → Devices → Print routing).
  const diagnosticReceiptPrinter = getDiagnosticReceiptPrinter();
  const { activeDevice } = usePrinterTab(diagnosticReceiptPrinter?.id);

  const filters = useMemo(() => ({
    search: debouncedSearch || undefined,
    status: status || undefined,
    paymentMode: paymentMode || undefined,
    paymentStatus: paymentStatus || undefined,
    isReferral: isReferral || undefined,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    dateBasis: dateFrom || dateTo ? dateBasis : undefined,
  }), [debouncedSearch, status, paymentMode, paymentStatus, isReferral, dateFrom, dateTo, dateBasis]);

  const { data, isLoading, error } = useDiagnosticReceipts(page, limit, filters);
  const receipts = useMemo(() => data?.data || [], [data]);
  const meta = data?.meta;
  const trashedCount = Number(data?.stats?.trashed ?? 0);

  // Full receipt (including its payments) for the open details modal, so the
  // payment history can show exactly when the receipt and any due were paid.
  const { data: detailReceiptDetail } = useDiagnosticReceipt(detailReceipt?.id);
  const detailPayments = useMemo<any[]>(() => {
    const raw = (detailReceiptDetail?.data as any)?.payments;
    if (!Array.isArray(raw)) return [];
    return [...raw].sort(
      (a: any, b: any) => new Date(a.paidAt).getTime() - new Date(b.paidAt).getTime(),
    );
  }, [detailReceiptDetail]);
  const detailPaymentsTotal = useMemo(
    () => detailPayments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0),
    [detailPayments],
  );

  // Closing the details modal also closes the referral calculation it opened.
  useEffect(() => {
    if (!detailReceipt) setShowDetailReferralInfo(false);
  }, [detailReceipt]);

  useEffect(() => {
    const newParams = new URLSearchParams();
    if (page > 1) newParams.set('page', String(page));
    if (debouncedSearch) newParams.set('search', debouncedSearch);
    if (status) newParams.set('status', status);
    if (paymentMode) newParams.set('paymentMode', paymentMode);
    if (paymentStatus) newParams.set('paymentStatus', paymentStatus);
    if (isReferral) newParams.set('isReferral', isReferral);
    if (dateFrom) newParams.set('dateFrom', dateFrom);
    if (dateTo) newParams.set('dateTo', dateTo);
    if (dateFrom || dateTo) newParams.set('dateBasis', dateBasis);
    const query = newParams.toString();
    navigate(`${location.pathname}${query ? `?${query}` : ''}`, { replace: true });
  }, [page, debouncedSearch, status, paymentMode, paymentStatus, isReferral, dateFrom, dateTo, dateBasis, navigate, location.pathname]);

  useEffect(() => {
    setPage(1);
  }, [search, status, paymentMode, paymentStatus, isReferral, dateFrom, dateTo, dateBasis]);

  // Drop selected ids that are no longer visible (e.g. after paging/filtering).
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const visible = new Set(receipts.map((r: any) => r.id));
      const next = new Set<string>();
      let changed = false;
      prev.forEach((id) => {
        if (visible.has(id)) next.add(id);
        else changed = true;
      });
      return changed ? next : prev;
    });
  }, [receipts]);

  const allSelected = receipts.length > 0 && receipts.every((r: any) => selectedIds.has(r.id));
  const someSelected = selectedIds.size > 0;

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = someSelected && !allSelected;
    }
  }, [someSelected, allSelected]);

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        receipts.forEach((r: any) => next.delete(r.id));
        return next;
      });
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        receipts.forEach((r: any) => next.add(r.id));
        return next;
      });
    }
  };

  const toggleSelectOne = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handlePrint = async (id: string) => {
    setPrintError(null);
    setPrintingId(id);
    try {
      const url = await diagnosticService.printReceipt(id);
      await printPdfToDevice(url, activeDevice, { onError: setPrintError });
    } catch (err: any) {
      setPrintError(err?.message || 'Failed to generate print preview.');
    } finally {
      setPrintingId(null);
    }
  };

  const handleDownload = async (id: string, receiptNumber: string) => {
    setPrintError(null);
    setDownloadingId(id);
    try {
      const url = await diagnosticService.downloadReceipt(id);
      const link = document.createElement('a');
      link.href = url;
      link.download = `receipt-${receiptNumber || id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setPrintError(err?.message || 'Failed to download PDF.');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleConfirmTrash = async () => {
    if (!trashConfirm) return;
    setTrashError(null);
    const ids =
      trashConfirm.mode === 'bulk' ? Array.from(selectedIds) : [trashConfirm.receiptId];
    try {
      const res: any = await trashMutation.mutateAsync(ids);
      const trashed = res?.data?.trashed?.length ?? ids.length;
      const blocked = res?.data?.blocked?.length ?? 0;
      if (blocked > 0) {
        toast.warning(`Moved ${trashed} receipt(s) to trash. ${blocked} could not be moved.`);
      } else {
        toast.success(`Moved ${trashed} receipt${trashed === 1 ? '' : 's'} to trash`);
      }
      setSelectedIds(new Set());
      setTrashConfirm(null);
    } catch (err: any) {
      const message = err?.message || 'Failed to move receipt(s) to trash. Please try again.';
      if (trashConfirm.mode === 'single') setTrashError(message);
      else toast.error(message);
    }
  };

  const handleConfirmRiskFund = async () => {
    if (!riskFundConfirm) return;
    const reason = riskFundReason.trim();
    if (!reason) {
      setRiskFundError('Enter a reason for moving the balance to the Risk Fund.');
      return;
    }
    setRiskFundError(null);
    const ids =
      riskFundConfirm.mode === 'bulk' ? Array.from(selectedIds) : [riskFundConfirm.receiptId];
    const isSingle = riskFundConfirm.mode === 'single';
    const maxAmount = isSingle ? Number(riskFundConfirm.dueAmount) || 0 : 0;
    const partialAmount = isSingle && riskFundAmount.trim() !== ''
      ? Number(riskFundAmount)
      : undefined;

    if (isSingle && partialAmount !== undefined) {
      if (
        !Number.isFinite(partialAmount) ||
        partialAmount <= 0 ||
        partialAmount > maxAmount + 0.001
      ) {
        setRiskFundError(
          `Enter an amount between ৳0.01 and ৳${maxAmount.toLocaleString()}.`,
        );
        return;
      }
    }

    try {
      const res: any = await moveToRiskFund.mutateAsync({
        receiptIds: ids,
        reason,
        ...(isSingle && partialAmount !== undefined ? { amount: partialAmount } : {}),
      });
      const movedCount = res?.data?.moved?.length ?? 0;
      const skipped = res?.data?.skipped ?? [];
      const totalAmount = Number(res?.data?.totalAmount) || 0;

      if (movedCount === 0) {
        setRiskFundError(
          skipped[0]?.reason ||
            'None of the selected receipts had an outstanding balance to move.',
        );
        return;
      }

      toast.success(
        `Moved ${movedCount} receipt${movedCount === 1 ? '' : 's'} to the Risk Fund (৳${totalAmount.toLocaleString()})`,
      );
      if (skipped.length > 0) {
        toast.warning(
          `${skipped.length} receipt${skipped.length === 1 ? ' was' : 's were'} skipped: ${skipped[0]?.reason}`,
        );
      }
      setSelectedIds(new Set());
      setRiskFundConfirm(null);
      setRiskFundReason('');
      setRiskFundAmount('');
      setDetailReceipt(null);
    } catch (err: any) {
      setRiskFundError(
        err?.message || 'Failed to move receipt(s) to Risk Fund. Please try again.',
      );
    }
  };

  /* ── Due payment modal derived figures ──────────────────────────────────
   * The extra discount mirrors the create/edit form: a percentage is applied
   * to the receipt's gross total, a flat amount is subtracted directly. The
   * item-level discounts already baked into the receipt are isolated from the
   * stored combined discount so re-applying the extra discount never
   * double-counts it. */
  const duePaymentGross = Number(duePaymentReceipt?.totalAmount) || 0;
  // The patient keeps the full discount. The referral commission is a hospital
  // expense, so it is never added back to the outstanding balance.
  const duePaymentReferralPercent =
    duePaymentReceipt?.referralId
      ? Math.max(
          0,
          Math.min(
            100,
            Number(
              duePaymentReceipt?.referralSharePercent ??
                duePaymentReceipt?.referral?.defaultSharePercent,
            ) || 0,
          ),
        )
      : 0;
  const duePaymentItemDiscount = Math.max(
    0,
    (Number(duePaymentReceipt?.discountAmount) || 0) -
      (Number(duePaymentReceipt?.extraDiscountAmount) || 0),
  );
  // The largest extra discount that can still be applied without the combined
  // discount exceeding the gross total (which the backend rejects).
  const duePaymentMaxExtraDiscount = Math.max(0, duePaymentGross - duePaymentItemDiscount);
  const duePaymentExtraDiscount = (() => {
    const value = Math.max(0, Number(duePaymentDiscountValue) || 0);
    if (value <= 0) return 0;
    if (duePaymentDiscountType === 'PERCENT') {
      return Number(((duePaymentGross * Math.min(value, 100)) / 100).toFixed(2));
    }
    return Number(Math.min(value, duePaymentMaxExtraDiscount).toFixed(2));
  })();
  // Raw input sent to the API, clamped to a value the backend accepts.
  const duePaymentDiscountPayloadValue =
    duePaymentDiscountType === 'PERCENT'
      ? Math.min(Math.max(0, Number(duePaymentDiscountValue) || 0), 100)
      : Math.min(Math.max(0, Number(duePaymentDiscountValue) || 0), duePaymentMaxExtraDiscount);
  const duePaymentCombinedDiscount = Number(
    (duePaymentItemDiscount + duePaymentExtraDiscount).toFixed(2),
  );
  // Informational only — the commission is paid by the hospital out of the
  // allocated base. Base = discount-eligible (test) gross, i.e. accessories are
  // excluded; fall back to the receipt total when items are not loaded.
  const duePaymentTestGross = (() => {
    const items = (duePaymentReceipt?.items || []).filter((item: any) => !item.isAccessory);
    const gross = items.reduce(
      (sum: number, item: any) =>
        sum + Number(item.unitPrice || 0) * Math.max(1, Number(item.quantity) || 1),
      0,
    );
    return gross > 0 ? gross : duePaymentGross;
  })();
  const duePaymentReferralCommission = Math.min(
    Math.round((duePaymentTestGross * duePaymentReferralPercent) / 100),
    Math.floor(duePaymentTestGross + 1e-9),
  );
  const duePaymentPatientDiscount = duePaymentCombinedDiscount;
  const duePaymentNetPayable = Math.max(
    0,
    Number((duePaymentGross - duePaymentPatientDiscount).toFixed(2)),
  );
  const duePaymentCurrentPaid = Number(duePaymentReceipt?.paidAmount) || 0;
  // Outstanding balance once the edited extra discount is taken into account.
  const duePaymentOutstanding = Math.max(
    0,
    Number((duePaymentNetPayable - duePaymentCurrentPaid).toFixed(2)),
  );
  const duePaymentRemaining = Math.max(
    0,
    Number((duePaymentOutstanding - (Number(duePaymentAmount) || 0)).toFixed(2)),
  );

  // Keep "Amount Received" pinned to the outstanding balance while the user
  // hasn't typed an amount themselves, so changing the discount updates it.
  useEffect(() => {
    if (!duePaymentReceipt || duePaymentAmountDirty) return;
    setDuePaymentAmount(duePaymentOutstanding);
  }, [duePaymentReceipt, duePaymentAmountDirty, duePaymentOutstanding]);

  const openDuePayment = (receipt: any) => {
    setDuePaymentReceipt(receipt);
    setDuePaymentAmount(Math.max(0, Number(receipt?.dueAmount) || 0));
    setDuePaymentAmountDirty(false);
    setDuePaymentMode(receipt?.paymentMode || 'cash');
    // Restore the saved extra-discount input. Legacy receipts only carry the
    // resolved amount, so fall back to treating it as a flat discount.
    const storedType = receipt?.extraDiscountType === 'PERCENT' ? 'PERCENT' : 'FLAT';
    const storedValue = Number(receipt?.extraDiscountValue || 0);
    const legacyAmount = Number(receipt?.extraDiscountAmount || 0);
    setDuePaymentDiscountType(storedType);
    setDuePaymentDiscountValue(storedValue > 0 ? storedValue : legacyAmount);
    setDuePaymentReference('');
    setDuePaymentDate(toDatetimeLocalValueInTimeZone(new Date(), hospitalTimezone));
    setDuePaymentDueDate(
      receipt?.dueDate ? String(receipt.dueDate).slice(0, 10) : '',
    );
    setDuePaymentError(null);
  };

  const closeDuePayment = () => {
    setDuePaymentReceipt(null);
    setDuePaymentError(null);
  };

  const handleRecordDuePayment = async () => {
    if (!duePaymentReceipt) return;
    const amount = Number(duePaymentAmount) || 0;
    if (duePaymentCombinedDiscount > duePaymentGross) {
      setDuePaymentError('Total discount cannot exceed the total amount.');
      return;
    }
    if (amount <= 0) {
      setDuePaymentError('Enter an amount greater than zero.');
      return;
    }
    if (amount > duePaymentOutstanding) {
      setDuePaymentError(
        `Amount cannot exceed the outstanding due of ৳${duePaymentOutstanding.toLocaleString()}.`,
      );
      return;
    }
    setDuePaymentError(null);
    const newPaidAmount = Number((duePaymentCurrentPaid + amount).toFixed(2));
    try {
      const res: any = await recordPaymentMutation.mutateAsync({
        id: duePaymentReceipt.id,
        data: {
          amount,
          paymentMode: duePaymentMode,
          transactionReference: duePaymentReference.trim() || undefined,
          paymentDate: duePaymentDate
            ? datetimeLocalToISOInTimeZone(duePaymentDate, hospitalTimezone)
            : undefined,
          // Only meaningful while a balance remains after this collection.
          dueDate:
            duePaymentRemaining > 0 && duePaymentDueDate
              ? duePaymentDueDate
              : undefined,
          extraDiscountType: duePaymentDiscountType,
          extraDiscountValue: duePaymentDiscountPayloadValue,
        },
      });
      const updated = res?.data;
      const newDue = updated?.dueAmount ?? duePaymentRemaining;
      toast.success(`Due payment of ৳${amount.toLocaleString()} recorded`);
      // Reflect the new balance in the still-open details modal without a reload.
      setDetailReceipt((prev: any) =>
        prev && prev.id === duePaymentReceipt.id
          ? {
              ...prev,
              paidAmount: updated?.paidAmount ?? newPaidAmount,
              dueAmount: newDue,
              discountAmount: updated?.discountAmount ?? duePaymentCombinedDiscount,
              extraDiscountAmount: updated?.extraDiscountAmount ?? duePaymentExtraDiscount,
              extraDiscountType: duePaymentDiscountType,
              extraDiscountValue: duePaymentDiscountPayloadValue,
              paidAt: updated?.paidAt ?? prev.paidAt,
              paymentMode: duePaymentMode,
            }
          : prev,
      );
      closeDuePayment();
    } catch (err: any) {
      setDuePaymentError(err?.message || 'Failed to record the payment. Please try again.');
    }
  };

  const clearFilters = () => {
    setSearch('');
    setStatus('');
    setPaymentMode('');
    setPaymentStatus('');
    setIsReferral('');
    setDateFrom('');
    setDateTo('');
    setDateBasis('document');
    setPage(1);
  };

  const hasActiveFilters = search || status || paymentMode || paymentStatus || isReferral || dateFrom || dateTo;

  // Discount-eligible (test) gross. Accessories carry no discount and are not
  // part of the referral commission base.
  const testGrossOf = (receipt: any) => {
    const items = (receipt?.items || []).filter((item: any) => !item.isAccessory);
    const gross = items.reduce(
      (sum: number, item: any) =>
        sum + Number(item.unitPrice || 0) * Math.max(1, Number(item.quantity) || 1),
      0,
    );
    return gross > 0 ? gross : Math.max(0, Number(receipt?.totalAmount) || 0);
  };

  // Referral commission: the referral % of the test gross. It is a hospital
  // expense, so it never reduces the patient's discount or raises the due.
  const referralCommissionOf = (receipt: any) => {
    if (!receipt?.referralId) return 0;
    const percent = Math.max(
      0,
      Math.min(
        100,
        Number(
          receipt?.referralSharePercent ?? receipt?.referral?.defaultSharePercent,
        ) || 0,
      ),
    );
    const gross = testGrossOf(receipt);
    // Whole-taka payout, capped at the commission base (backend parity).
    return percent > 0
      ? Math.min(
          Math.round((gross * percent) / 100),
          Math.floor(gross + 1e-9),
        )
      : 0;
  };

  // Paid Amount column = the amount paid on the receipt.
  const paidValue = (receipt: any) => Number(receipt?.paidAmount) || 0;

  // Total = Paid Amount + Due.
  const totalValue = (receipt: any) =>
    paidValue(receipt) + (Number(receipt?.dueAmount) || 0);

  /**
   * Discount & referral breakdown for the open details modal. The patient keeps
   * the full entered discount; the referral partner is paid by the hospital, so
   * the commission never appears as a reduction of the patient's discount or as
   * an addition to the payable.
   */
  const detailBreakdown = useMemo(() => {
    const receipt = detailReceipt;
    const gross = Number(receipt?.totalAmount) || 0;
    const extraDiscountAmount = Math.max(0, Number(receipt?.extraDiscountAmount) || 0);
    const combinedDiscountAmount = Math.max(0, Number(receipt?.discountAmount) || 0);
    const itemDiscountAmount = Math.max(
      0,
      Number((combinedDiscountAmount - extraDiscountAmount).toFixed(2)),
    );
    // Accessories carry no discount, so the rate is based on the test gross.
    const testGross = (receipt?.items || [])
      .filter((item: any) => !item.isAccessory)
      .reduce(
        (sum: number, item: any) =>
          sum + Number(item.unitPrice || 0) * Math.max(1, Number(item.quantity) || 1),
        0,
      );
    const percentOfGross = (amount: number) =>
      gross > 0 ? Number(((amount / gross) * 100).toFixed(2)) : 0;
    const percentOfTests = (amount: number) =>
      testGross > 0 ? Number(((amount / testGross) * 100).toFixed(2)) : 0;
    const referralPercent = receipt?.referralId
      ? Math.round(
          Math.max(
            0,
            Math.min(
              100,
              Number(
                receipt?.referralSharePercent ?? receipt?.referral?.defaultSharePercent,
              ) || 0,
            ),
          ) * 100,
        ) / 100
      : 0;
    const referralCommissionAmount = referralCommissionOf(receipt);
    const patientItemDiscountAmount = itemDiscountAmount;
    const patientDiscountAmount = combinedDiscountAmount;
    // Same per-test rows the calculation dialog shows on the edit page.
    const lines: Array<{
      name: string;
      qty: number;
      gross: number;
      discountPercent: number;
      discount: number;
      share: number;
    }> = (receipt?.items || [])
      .filter((item: any) => !item.isAccessory && (item.diagnosticTestId || item.test?.id))
      .map((item: any) => {
        const qty = Math.max(1, Number(item.quantity) || 1);
        const lineGross = Number((Number(item.unitPrice || 0) * qty).toFixed(2));
        const discountPercent = Math.max(0, Math.min(100, Number(item.discountPercent) || 0));
        const lineDiscount =
          Number(item.discountAmount) > 0
            ? Number(item.discountAmount)
            : Number(((lineGross * discountPercent) / 100).toFixed(2));
        return {
          name: item.test?.name || item.testName || 'Test',
          qty,
          gross: lineGross,
          discountPercent,
          discount: lineDiscount,
          share: Math.min(
            lineGross,
            Number(((lineGross * referralPercent) / 100).toFixed(2)),
          ),
        };
      });
    // Spread the rounded payout across the lines so the dialog sums exactly.
    const allocation = allocateWholeTaka(
      referralCommissionAmount,
      lines.map((line) => line.share),
    );
    lines.forEach((line, index) => {
      line.share = allocation[index] ?? 0;
    });
    return {
      gross,
      testGross,
      // Patient payable: gross minus the full discount. The commission is a
      // hospital expense and is not added back.
      netPayableAmount: Math.max(0, Number((gross - patientDiscountAmount).toFixed(2))),
      itemDiscountAmount,
      itemDiscountPercent: percentOfTests(itemDiscountAmount),
      extraDiscountAmount,
      combinedDiscountAmount,
      combinedDiscountPercent: percentOfTests(combinedDiscountAmount),
      referralPercent,
      referralCommissionAmount,
      referralPercentOfBill: percentOfGross(referralCommissionAmount),
      patientItemDiscountAmount,
      patientItemDiscountPercent: percentOfTests(patientItemDiscountAmount),
      patientDiscountAmount,
      lines,
      // Hospital margin after the patient discount and the referral payout.
      hospitalShare: Math.max(
        0,
        Number((gross - combinedDiscountAmount - referralCommissionAmount).toFixed(2)),
      ),
    };
  }, [detailReceipt]);

  // Referral icon/filter track ONLY the canonical referral partner
  // (`referralId`). The reference-only doctor (`doctorReference*`) is a
  // separate, informational field and must NOT mark a receipt as a referral.
  const isReferralReceipt = (receipt: any): boolean =>
    !!(receipt.referral?.id || receipt.referral?.name || receipt.referralId);

  // Single "Ref:" line for the Patient column, mirroring the printed receipt's
  // "Refd by": prefer the reference-only doctor (with qualifications), then the
  // canonical referral partner, then the legacy referring-doctor snapshot.
  const referralDisplay = (receipt: any): string | null => {
    const doctorRefName = receipt.doctorReference?.name || receipt.doctorReferenceName;
    if (doctorRefName) {
      const quals = (receipt.doctorReference?.qualifications || []).filter(Boolean).join(', ');
      return quals ? `${doctorRefName} ${quals}` : doctorRefName;
    }
    const referralName = receipt.referral?.name || receipt.referringDoctorName;
    if (!referralName) return null;
    const detail = receipt.referral
      ? [receipt.referral.designation, receipt.referral.specialization, receipt.referral.organization]
          .filter(Boolean)
          .join(', ')
      : [
          receipt.referringDoctor?.qualifications?.join(', '),
          receipt.referringDoctor?.department?.name,
        ]
          .filter(Boolean)
          .join(', ');
    return detail ? `${referralName} ${detail}` : referralName;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
        <AlertCircle size={18} />
        <span>Failed to load diagnostic receipts</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ClipboardList className="w-6 h-6 text-primary" />
          <h1 className="text-2xl font-bold text-text">Diagnostic Receipts</h1>
        </div>
        <div className="flex items-center gap-2">
          {canDelete && (
            <Link
              to="/opd/diagnostic-receipts/trash"
              className="px-4 py-2 border border-border text-text-muted hover:text-danger hover:border-danger rounded-lg transition-colors text-sm font-medium flex items-center gap-2"
              title="View trashed receipts"
            >
              <Trash2 size={16} />
              Trash
              {trashedCount > 0 && (
                <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-danger text-white text-[0.625rem] font-semibold leading-none">
                  {trashedCount > 99 ? '99+' : trashedCount}
                </span>
              )}
            </Link>
          )}
          <button
            onClick={() => navigate('/opd/diagnostic-receipts/new')}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium flex items-center gap-2"
          >
            <Plus size={16} />
            New Receipt
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 text-xs text-text-muted">
        <span className="inline-flex items-center gap-1.5">
          <Printer size={13} />
          Print to:
        </span>
        <PrinterTabs defaultId={diagnosticReceiptPrinter?.id} />
      </div>

      {justCreated && (
        <div className="flex items-center gap-2 p-4 bg-success/10 border border-success/20 rounded-lg text-success text-sm">
          <CheckCircle2 size={18} />
          <span>Receipt created successfully.</span>
        </div>
      )}

      {printError && (
        <div className="flex items-start gap-2 p-3 bg-danger/10 border border-danger/20 rounded-lg text-sm text-danger">
          <AlertCircle size={16} className="shrink-0 mt-0.5" />
          <span>{printError}</span>
        </div>
      )}

      {/* Search & Filters */}
      <div className="bg-surface rounded-xl border border-border p-4 space-y-3">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
            <input
              type="text"
              placeholder="Search by receipt number or patient..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-background border border-border rounded-lg text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowFilters((v) => !v)}
              className={`px-3 py-2 border rounded-lg transition-colors text-sm flex items-center gap-1.5 whitespace-nowrap ${
                showFilters || hasActiveFilters
                  ? 'bg-primary/10 border-primary text-primary'
                  : 'text-text-muted hover:text-text border-border'
              }`}
            >
              <SlidersHorizontal size={14} />
              Filters
              {hasActiveFilters && (
                <span className="ml-0.5 w-5 h-5 rounded-full bg-primary text-white text-[0.625rem] font-bold flex items-center justify-center">
                  {[status, paymentMode, paymentStatus, isReferral, dateFrom, dateTo].filter(Boolean).length + (search ? 1 : 0)}
                </span>
              )}
            </button>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="px-3 py-2 text-text-muted hover:text-danger border border-border hover:border-danger rounded-lg transition-colors text-sm flex items-center gap-1.5 whitespace-nowrap"
              >
                <X size={14} />
                Clear
              </button>
            )}
          </div>
        </div>

        {showFilters && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 pt-2 border-t border-border">
            <div>
              <label className="block text-xs font-medium text-text-muted mb-1">Referral</label>
              <select
                value={isReferral}
                onChange={(e) => setIsReferral(e.target.value as 'true' | 'false' | '')}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/50"
                title="Show only referral receipts or only walk-in (direct) receipts"
              >
                <option value="">All Receipts</option>
                <option value="true">Referral Receipts</option>
                <option value="false">Direct (No Referral)</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-text-muted mb-1">Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/50"
              >
                <option value="">All Statuses</option>
                {STATUSES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-text-muted mb-1">Payment Mode</label>
              <select
                value={paymentMode}
                onChange={(e) => setPaymentMode(e.target.value)}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/50"
              >
                <option value="">All Modes</option>
                {PAYMENT_MODES.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-text-muted mb-1">Payment Status</label>
              <select
                value={paymentStatus}
                onChange={(e) => setPaymentStatus(diagnosticPaymentStatusFilter(e.target.value))}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/50"
              >
                <option value="">All Payment Statuses</option>
                {PAYMENT_STATUSES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-text-muted mb-1">Date Basis</label>
              <select
                value={dateBasis}
                onChange={(e) => setDateBasis(e.target.value as 'document' | 'settled')}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/50"
                title="Which date the range below applies to"
              >
                <option value="document">Created Date</option>
                <option value="settled">Settled On</option>
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-text-muted mb-1">
                {dateBasis === 'settled' ? 'Settled On Range' : 'Date Range'}
              </label>
              <div className="flex items-center gap-2">
                <DatePicker value={dateFrom} onChange={setDateFrom} />
                <span className="text-text-muted text-sm">-</span>
                <DatePicker value={dateTo} onChange={setDateTo} />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Bulk actions */}
      {someSelected && canSelectReceipts && (
        <div className="bg-primary/5 border border-primary/20 rounded-xl p-3 shadow-sm flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-text">
            <span className="font-medium">{selectedIds.size}</span> selected
          </div>
          <div className="flex items-center gap-2">
            {canManageRiskFund && (
              <button
                onClick={() => {
                  setRiskFundAmount('');
                  setRiskFundError(null);
                  setRiskFundConfirm({ mode: 'bulk' });
                }}
                disabled={moveToRiskFund.isPending}
                className="px-3 py-1.5 rounded-lg bg-warning text-white text-sm font-medium hover:bg-warning/90 transition-colors disabled:opacity-50 flex items-center gap-1.5"
              >
                <AlertTriangle size={14} />
                Move to Risk Fund
              </button>
            )}
            {canDelete && (
              <button
                onClick={() => setTrashConfirm({ mode: 'bulk' })}
                disabled={trashMutation.isPending}
                className="px-3 py-1.5 rounded-lg bg-danger text-white text-sm font-medium hover:bg-danger/90 transition-colors disabled:opacity-50 flex items-center gap-1.5"
              >
                {trashMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                Move to trash
              </button>
            )}
            <button
              onClick={() => setSelectedIds(new Set())}
              disabled={trashMutation.isPending}
              className="px-3 py-1.5 text-sm text-text-muted hover:text-text border border-border rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1.5"
            >
              <X size={14} />
              Clear selection
            </button>
          </div>
        </div>
      )}

      {/* Receipts List */}
      <div className="bg-surface rounded-xl border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-background/50">
                {canSelectReceipts && (
                  <th className="px-4 py-3 w-10 text-left">
                    <input
                      ref={headerCheckboxRef}
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleSelectAll}
                      disabled={receipts.length === 0}
                      className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer disabled:cursor-default"
                      title="Select all"
                    />
                  </th>
                )}
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Receipt #</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Patient</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Tests</th>
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase"
                  title="Amount paid on this receipt"
                >
                  Paid Amount
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Due</th>
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase"
                  title="Paid Amount + Due"
                >
                  Total
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase"
                  title="Date the balance was paid in full"
                >
                  Settled On
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Payment</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Created Date</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {receipts.length === 0 ? (
                <tr>
                  <td colSpan={canSelectReceipts ? 11 : 10} className="px-4 py-12 text-center text-text-muted">
                    {hasActiveFilters ? (
                      <span>No receipts match your filters.{' '}
                        <button onClick={clearFilters} className="text-primary hover:underline">
                          Clear filters
                        </button>
                      </span>
                    ) : (
                      'No diagnostic receipts found'
                    )}
                  </td>
                </tr>
              ) : (
                receipts.map((receipt: any) => (
                  <tr key={receipt.id} className="hover:bg-background/50 transition-colors">
                    {canSelectReceipts && (
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(receipt.id)}
                          onChange={() => toggleSelectOne(receipt.id)}
                          className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer"
                        />
                      </td>
                    )}
                    <td className="px-4 py-3 text-sm font-mono text-text">
                      <button
                        onClick={() => {
                          setDetailTab('tests');
                          setDetailReceipt(receipt);
                        }}
                        className="hover:text-primary hover:underline transition-colors"
                        title="View Receipt Details"
                      >
                        {receipt.receiptNumber}
                        {isReferralReceipt(receipt) && (
                          <span
                            className="inline-flex items-center justify-center w-[18px] h-[18px] rounded-full bg-primary/10 text-primary ring-1 ring-primary/20 ml-1 align-middle"
                            title={`Referral receipt${receipt.referral?.name ? ` — ${receipt.referral.name}` : ''}`}
                            aria-label="Referral receipt"
                          >
                            <UserPlus size={10} />
                          </span>
                        )}
                      </button>
                      {(() => {
                        const preparedBy = `${receipt.createdBy?.firstName || ''} ${receipt.createdBy?.lastName || ''}`.trim();
                        return preparedBy ? (
                          <div className="text-xs text-text-muted mt-0.5 font-sans">
                            Prepared by: {preparedBy}
                          </div>
                        ) : null;
                      })()}
                    </td>
                    <td className="px-4 py-3 text-sm text-text">
                      <button
                        onClick={() => {
                          const patientId = receipt.patient?.id || receipt.patientId;
                          navigate(patientId ? `/patients/${patientId}` : `/opd/diagnostic-receipts/${receipt.id}`);
                        }}
                        className="hover:text-primary hover:underline transition-colors text-left"
                        title="View Patient Details"
                      >
                        {joinFullName(receipt.patient || {})}
                      </button>
                      {(() => {
                        const ref = referralDisplay(receipt);
                        return ref ? (
                          <div className="text-xs text-text-muted mt-0.5 flex items-center gap-1">
                            <Stethoscope size={10} /> Ref: {ref}
                          </div>
                        ) : null;
                      })()}
                    </td>
                    <td className="px-4 py-3 text-sm text-text-muted">
                      {(receipt.items || []).filter((item: any) => !item.isAccessory).length} test(s)
                    </td>
                    <td className="px-4 py-3 text-sm font-semibold text-success">৳{paidValue(receipt).toLocaleString()}</td>
                    <td className="px-4 py-3 text-sm font-medium text-danger">৳{Number(receipt.dueAmount || 0).toLocaleString()}</td>
                    <td className="px-4 py-3 text-sm font-medium text-text">৳{totalValue(receipt).toLocaleString()}</td>
                    <td className="px-4 py-3 text-sm text-text-muted">
                      {Number(receipt.dueAmount || 0) <= 0 && receipt.paidAt
                        ? new Date(receipt.paidAt).toLocaleDateString()
                        : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <PaymentStatusBadge status={paymentStatusOf(receipt)} />
                    </td>
                    <td className="px-4 py-3 text-sm text-text-muted">
                      {(() => {
                        const d = new Date(receipt.createdAt);
                        const day = d.getDate();
                        const month = d.toLocaleString('en-GB', { month: '2-digit' });
                        const year = d.getFullYear();
                        let hours = d.getHours();
                        const minutes = String(d.getMinutes()).padStart(2, '0');
                        const ampm = hours >= 12 ? 'pm' : 'am';
                        hours = hours % 12;
                        hours = hours ? hours : 12;
                        return `${String(day).padStart(2, '0')}/${month}/${year}, ${String(hours).padStart(2, '0')}:${minutes} ${ampm}`;
                      })()}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => navigate(`/opd/diagnostic-receipts/${receipt.id}`)}
                          className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-primary transition-colors"
                          title="View / Edit"
                        >
                          <ExternalLink size={16} />
                        </button>
                        <button
                          onClick={() => handlePrint(receipt.id)}
                          disabled={printingId === receipt.id}
                          className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-primary transition-colors disabled:opacity-50"
                          title="Print Receipt"
                        >
                          {printingId === receipt.id ? (
                            <Loader2 size={16} className="animate-spin" />
                          ) : (
                            <Printer size={16} />
                          )}
                        </button>
                        <button
                          onClick={() => handleDownload(receipt.id, receipt.receiptNumber)}
                          disabled={downloadingId === receipt.id}
                          className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-primary transition-colors disabled:opacity-50"
                          title="Download PDF"
                        >
                          {downloadingId === receipt.id ? (
                            <Loader2 size={16} className="animate-spin" />
                          ) : (
                            <Download size={16} />
                          )}
                        </button>
                        {canManageRiskFund && Number(receipt.dueAmount || 0) > 0 && (
                          <button
                            onClick={() =>
                              {
                                const due = Number(receipt.dueAmount) || 0;
                                setRiskFundAmount(String(due));
                                setRiskFundError(null);
                                setRiskFundConfirm({
                                  mode: 'single',
                                  receiptId: receipt.id,
                                  receiptNumber: receipt.receiptNumber,
                                  dueAmount: due,
                                });
                              }
                            }
                            className="p-1.5 rounded-lg hover:bg-warning/10 text-text-muted hover:text-warning transition-colors"
                            title="Move to Risk Fund"
                          >
                            <AlertTriangle size={16} />
                          </button>
                        )}
                        {canDelete && (
                          <button
                            onClick={() =>
                              setTrashConfirm({
                                mode: 'single',
                                receiptId: receipt.id,
                                receiptNumber: receipt.receiptNumber,
                              })
                            }
                            className="p-1.5 rounded-lg hover:bg-danger/10 text-text-muted hover:text-danger transition-colors"
                            title="Move to trash"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          page={page}
          totalPages={meta?.totalPages || 1}
          total={meta?.total || 0}
          limit={limit}
          onPageChange={setPage}
          itemName="receipts"
        />
      </div>

      <ConfirmDialog
        open={trashConfirm !== null}
        title={trashConfirm?.mode === 'bulk' ? 'Move receipts to trash' : 'Move receipt to trash'}
        description={
          trashConfirm?.mode === 'bulk' ? (
            `Move ${selectedIds.size} selected receipt(s) to the trash? You can restore them later.`
          ) : (
            <>
              Move receipt{' '}
              <strong>{trashConfirm?.mode === 'single' ? trashConfirm.receiptNumber : ''}</strong> to
              the trash? You can restore it later.
            </>
          )
        }
        confirmLabel="Move to trash"
        loading={trashMutation.isPending}
        onConfirm={handleConfirmTrash}
        onCancel={() => {
          setTrashConfirm(null);
          setTrashError(null);
        }}
      />

      <RiskFundMoveDialog
        open={riskFundConfirm !== null}
        title={
          riskFundConfirm?.mode === 'bulk'
            ? 'Move receipts to Risk Fund'
            : 'Move receipt to Risk Fund'
        }
        description={
          riskFundConfirm?.mode === 'bulk' ? (
            <>
              Move <strong>{selectedIds.size}</strong> selected receipt(s) to the
              Risk Fund? Their outstanding balances will leave billing due.
            </>
          ) : (
            <>
              Move receipt{' '}
              <strong>
                {riskFundConfirm?.mode === 'single' ? riskFundConfirm.receiptNumber : ''}
              </strong>{' '}
              to the Risk Fund? Its outstanding balance will leave billing due.
            </>
          )
        }
        reason={riskFundReason}
        onReasonChange={setRiskFundReason}
        maxAmount={
          riskFundConfirm?.mode === 'single'
            ? riskFundConfirm.dueAmount
            : undefined
        }
        amount={riskFundConfirm?.mode === 'single' ? riskFundAmount : undefined}
        onAmountChange={
          riskFundConfirm?.mode === 'single' ? setRiskFundAmount : undefined
        }
        error={riskFundError}
        loading={moveToRiskFund.isPending}
        confirmLabel={
          riskFundConfirm?.mode === 'single'
            ? `Move ৳${(Number(riskFundAmount) || 0).toLocaleString()} to Risk Fund`
            : 'Move to Risk Fund'
        }
        onConfirm={handleConfirmRiskFund}
        onCancel={() => {
          setRiskFundConfirm(null);
          setRiskFundReason('');
          setRiskFundAmount('');
          setRiskFundError(null);
        }}
      />

      {/* Receipt details modal — opened by clicking a receipt number */}
      {detailReceipt && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          onClick={() => setDetailReceipt(null)}
        >
          <div
            className="bg-surface rounded-2xl border border-border w-full max-w-2xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-start justify-between gap-4 px-6 py-5 border-b border-border bg-background/40">
              <div className="flex items-start gap-3 min-w-0">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                  <ClipboardList size={20} className="text-primary" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-lg font-bold text-text leading-tight">Receipt Details</h3>
                  <p className="text-sm font-mono text-text-muted mt-0.5 truncate">
                    {detailReceipt.receiptNumber}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <PaymentStatusBadge status={paymentStatusOf(detailReceipt)} />
                {/* A written-off receipt has both a payment state and a workflow
                    status of "Risk Fund" — showing both pills repeats the same
                    word twice, so only add the workflow pill when it says
                    something the payment badge does not. */}
                {diagnosticReceiptStatusLabel(detailReceipt.status) !==
                  DIAGNOSTIC_PAYMENT_STATUS_META[paymentStatusOf(detailReceipt)].label && (
                  <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${diagnosticReceiptStatusClass(detailReceipt.status)}`}>
                    {diagnosticReceiptStatusLabel(detailReceipt.status)}
                  </span>
                )}
                <button
                  onClick={() => setDetailReceipt(null)}
                  className="p-1.5 rounded-lg text-text-muted hover:text-text hover:bg-background transition-colors"
                  title="Close"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="overflow-y-auto px-6 py-5 space-y-5">
              {/* Meta */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <User size={16} className="text-text-muted mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs text-text-muted uppercase">Patient</p>
                    <button
                      type="button"
                      onClick={() => {
                        const patientId = detailReceipt.patient?.id || detailReceipt.patientId;
                        if (patientId) {
                          setDetailReceipt(null);
                          navigate(`/patients/${patientId}`);
                        }
                      }}
                      disabled={!(detailReceipt.patient?.id || detailReceipt.patientId)}
                      className={`text-sm font-medium text-text truncate text-left transition-colors ${
                        detailReceipt.patient?.id || detailReceipt.patientId
                          ? 'hover:text-primary hover:underline cursor-pointer'
                          : 'cursor-default'
                      }`}
                      title="View Patient Details"
                    >
                      {joinFullName(detailReceipt.patient || {})}
                    </button>
                    {detailReceipt.patient?.mobile && (
                      <p className="text-xs text-text-muted">{detailReceipt.patient.mobile}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Calendar size={16} className="text-text-muted mt-0.5 shrink-0" />
                  <div>
                    <p className="text-xs text-text-muted uppercase">Date</p>
                    <p className="text-sm font-medium text-text">
                      {new Date(detailReceipt.createdAt).toLocaleString()}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Stethoscope size={16} className="text-text-muted mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs text-text-muted uppercase">Referral</p>
                    <p className="text-sm font-medium text-text truncate">
                      {detailReceipt.referral?.name || detailReceipt.referringDoctorName || 'N/A'}
                    </p>
                    {(detailReceipt.referral?.organization || detailReceipt.referringDoctor?.department?.name) && (
                      <p className="text-xs text-text-muted">
                        {detailReceipt.referral?.organization || detailReceipt.referringDoctor?.department?.name}
                      </p>
                    )}
                  </div>
                </div>
                {detailReceipt.doctorReferenceName && (
                  <div className="flex items-start gap-3">
                    <Stethoscope size={16} className="text-text-muted mt-0.5 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-xs text-text-muted uppercase">Doctor Reference</p>
                      <p className="text-sm font-medium text-text truncate">
                        {detailReceipt.doctorReferenceName}
                      </p>
                    </div>
                  </div>
                )}
                <div className="flex items-start gap-3">
                  <User size={16} className="text-text-muted mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs text-text-muted uppercase">Prepared By</p>
                    <p className="text-sm font-medium text-text truncate">
                      {`${detailReceipt.createdBy?.firstName || ''} ${detailReceipt.createdBy?.lastName || ''}`.trim() || 'N/A'}
                    </p>
                    <p className="text-xs text-text-muted uppercase mt-1">Payment Mode</p>
                    <p className="text-sm font-medium text-text uppercase">
                      {detailReceipt.paymentMode || 'N/A'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Tabs: Tests & Items / Payment History */}
              <div className="flex w-fit items-center gap-1 rounded-lg border border-border bg-background p-1">
                <button
                  type="button"
                  onClick={() => setDetailTab('tests')}
                  className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                    detailTab === 'tests' ? 'bg-primary text-white' : 'text-text-muted hover:bg-surface hover:text-text'
                  }`}
                >
                  <FlaskConical size={15} />
                  Tests
                </button>
                <button
                  type="button"
                  onClick={() => setDetailTab('payments')}
                  className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                    detailTab === 'payments' ? 'bg-primary text-white' : 'text-text-muted hover:bg-surface hover:text-text'
                  }`}
                >
                  <Banknote size={15} />
                  Pay History
                </button>
              </div>

              {detailTab === 'tests' && (
                <>
              {/* Items */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <FlaskConical size={15} className="text-primary" />
                  <h4 className="text-xs font-semibold text-text-muted uppercase tracking-wide">
                    Tests &amp; Items
                  </h4>
                </div>
                <div className="rounded-xl border border-border overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-background border-b border-border">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-text-muted uppercase">Test</th>
                        <th className="px-3 py-2 text-center text-xs font-medium text-text-muted uppercase w-14">Qty</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-text-muted uppercase">Unit</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-text-muted uppercase">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {(detailReceipt.items || []).length === 0 ? (
                        <tr>
                          <td colSpan={4} className="px-3 py-6 text-center text-text-muted">
                            No items on this receipt
                          </td>
                        </tr>
                      ) : (
                        (detailReceipt.items || []).map((item: any) => (
                          <tr key={item.id} className="hover:bg-background/50">
                            <td className="px-3 py-2.5">
                              <p className="font-medium text-text text-xs">
                                {item.test?.name || item.testName || item.accessory?.name || item.accessoryName || 'Item'}
                              </p>
                              {(item.test?.department?.name || item.department?.name) && (
                                <p className="text-[0.625rem] text-text-muted">
                                  {item.test?.department?.name || item.department?.name}
                                </p>
                              )}
                            </td>
                            <td className="px-3 py-2.5 text-center text-text">{item.quantity ?? 1}</td>
                            <td className="px-3 py-2.5 text-right text-text-muted whitespace-nowrap">
                              ৳{Number(item.unitPrice || 0).toLocaleString()}
                            </td>
                            <td className="px-3 py-2.5 text-right font-medium text-text whitespace-nowrap">
                              <div>৳{Number(item.totalPrice || 0).toLocaleString()}</div>
                              {Number(item.discountPercent || 0) > 0 && (
                                <div className="text-[0.625rem] text-danger">
                                  -{item.discountPercent}%
                                </div>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Summary — mirrors the list columns: Paid Amount, Due, Total */}
              <div className="rounded-xl border border-border bg-background/40 p-4 space-y-2.5">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-text-muted">Gross Amount</span>
                  <span className="text-text font-medium">
                    ৳{Number(detailReceipt.totalAmount || 0).toLocaleString()}
                  </span>
                </div>
                {detailBreakdown.referralCommissionAmount > 0 ? (
                  <>
                    {detailBreakdown.itemDiscountAmount > 0 && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-text-muted">
                          Item discount (
                          <span className="text-primary font-semibold">
                            {detailBreakdown.itemDiscountPercent}%
                          </span>
                          )
                        </span>
                        <span className="text-text font-medium">
                          ৳{detailBreakdown.itemDiscountAmount.toLocaleString()}
                        </span>
                      </div>
                    )}
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-text-muted inline-flex items-center gap-1">
                        Referral · hospital pays (
                        <span className="text-primary font-semibold">
                          {Number(detailBreakdown.referralPercent.toFixed(2))}%
                        </span>{' '}
                        of the test bill
                        )
                        <button
                          type="button"
                          onClick={() => setShowDetailReferralInfo(true)}
                          aria-label="How is this referral amount calculated?"
                          className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-text-muted transition-colors hover:bg-primary/10 hover:text-primary"
                        >
                          <Info size={12} />
                        </button>
                      </span>
                      <span className="text-primary font-medium">
                        ৳{Math.round(detailBreakdown.referralCommissionAmount).toLocaleString()}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-text-muted">
                        Discount (
                        <span className="text-primary font-semibold">
                          {detailBreakdown.patientItemDiscountPercent}%
                        </span>
                        )
                      </span>
                      <span className="text-danger font-medium">
                        -৳{detailBreakdown.patientItemDiscountAmount.toLocaleString()}
                      </span>
                    </div>
                    {detailBreakdown.extraDiscountAmount > 0 && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-text-muted">
                          Extra Discount
                          {detailReceipt.extraDiscountType === 'PERCENT' && (
                            <>
                              {' ('}
                              <span className="text-primary font-semibold">
                                {Number(detailReceipt.extraDiscountValue) || 0}%
                              </span>
                              {')'}
                            </>
                          )}
                        </span>
                        <span className="text-danger font-medium">
                          -৳{detailBreakdown.extraDiscountAmount.toLocaleString()}
                        </span>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {detailBreakdown.itemDiscountAmount > 0 && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-text-muted">
                          Discount (
                          <span className="text-primary font-semibold">
                            {detailBreakdown.itemDiscountPercent}%
                          </span>
                          )
                        </span>
                        <span className="text-danger font-medium">
                          -৳{Number(detailBreakdown.itemDiscountAmount.toFixed(2)).toLocaleString()}
                        </span>
                      </div>
                    )}
                    {detailBreakdown.extraDiscountAmount > 0 && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-text-muted">
                          Extra Discount
                          {detailReceipt.extraDiscountType === 'PERCENT' && (
                            <>
                              {' ('}
                              <span className="text-primary font-semibold">
                                {Number(detailReceipt.extraDiscountValue) || 0}%
                              </span>
                              {')'}
                            </>
                          )}
                        </span>
                        <span className="text-danger font-medium">
                          -৳{detailBreakdown.extraDiscountAmount.toLocaleString()}
                        </span>
                      </div>
                    )}
                    {detailBreakdown.combinedDiscountAmount > 0 && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-text-muted">
                          Total Discount (
                          <span className="text-primary font-semibold">
                            {detailBreakdown.combinedDiscountPercent}%
                          </span>
                          )
                        </span>
                        <span className="text-danger font-bold">
                          -৳{detailBreakdown.combinedDiscountAmount.toLocaleString()}
                        </span>
                      </div>
                    )}
                  </>
                )}
                <div className="flex items-center justify-between text-sm">
                  <span className="text-text-muted">Net Amount</span>
                  <span className="text-text font-medium">
                    ৳{detailBreakdown.netPayableAmount.toLocaleString()}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-text-muted">Paid Amount</span>
                  <span className="text-success font-medium">
                    ৳{paidValue(detailReceipt).toLocaleString()}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-text-muted">Due</span>
                  <span className="text-danger font-medium">
                    ৳{Number(detailReceipt.dueAmount || 0).toLocaleString()}
                  </span>
                </div>
                {Number(detailReceipt.dueAmount || 0) > 0 && detailReceipt.dueDate && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-text-muted">Promised Due Date</span>
                    <span className="text-text font-medium">
                      {new Date(detailReceipt.dueDate).toLocaleDateString()}
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-between text-sm pt-2 border-t border-border">
                  <span className="text-text font-semibold">
                    Total <span className="text-text-muted font-normal">(Paid + Due)</span>
                  </span>
                  <span className="text-primary font-bold text-base">
                    ৳{totalValue(detailReceipt).toLocaleString()}
                  </span>
                </div>
              </div>
                </>
              )}

              {detailTab === 'payments' && (
                <>
                  {/* Payment history — records when the receipt was paid and when
                      any outstanding due was collected (each on its own day). */}
              {(() => {
                const initialPaid = Math.max(
                  0,
                  paidValue(detailReceipt) - detailPaymentsTotal,
                );
                const hasAny = initialPaid > 0 || detailPayments.length > 0;
                return (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Banknote size={15} className="text-primary" />
                      <h4 className="text-xs font-semibold text-text-muted uppercase tracking-wide">
                        Payment History
                      </h4>
                    </div>
                    <div className="rounded-xl border border-border overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-background border-b border-border">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-text-muted uppercase">Paid On</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-text-muted uppercase">Type</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-text-muted uppercase">Mode</th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-text-muted uppercase">Amount</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                          {initialPaid > 0 && (
                            <tr>
                              <td className="px-3 py-2.5 text-text-muted">
                                {new Date(detailReceipt.createdAt).toLocaleString()}
                              </td>
                              <td className="px-3 py-2.5 text-text">At counter</td>
                              <td className="px-3 py-2.5 text-text uppercase">
                                {detailReceipt.paymentMode || '—'}
                              </td>
                              <td className="px-3 py-2.5 text-right font-medium text-text">
                                ৳{initialPaid.toLocaleString()}
                              </td>
                            </tr>
                          )}
                          {detailPayments.map((payment: any) => (
                            <tr key={payment.id}>
                              <td className="px-3 py-2.5 text-text-muted">
                                {new Date(payment.paidAt).toLocaleString()}
                              </td>
                              <td className="px-3 py-2.5 text-text">Due payment</td>
                              <td className="px-3 py-2.5 text-text uppercase">
                                {payment.paymentMode || '—'}
                              </td>
                              <td className="px-3 py-2.5 text-right font-medium text-success">
                                ৳{Number(payment.amount || 0).toLocaleString()}
                              </td>
                            </tr>
                          ))}
                          {!hasAny && (
                            <tr>
                              <td colSpan={4} className="px-3 py-6 text-center text-text-muted">
                                No payments recorded yet
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                    {Number(detailReceipt.dueAmount || 0) <= 0 && detailReceipt.paidAt && (
                      <p className="text-[0.6875rem] text-success mt-1.5">
                        Fully paid on {new Date(detailReceipt.paidAt).toLocaleString()}
                      </p>
                    )}
                  </div>
                );
              })()}
                </>
              )}

              {detailTab === 'tests' && (
                <>
                  {detailReceipt.doctorRecommendation && (
                    <div className="rounded-xl border border-border bg-background/40 p-3">
                      <p className="text-xs text-text-muted uppercase mb-1">Notes</p>
                      <p className="text-sm text-text">{detailReceipt.doctorRecommendation}</p>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Footer actions */}
            <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-border bg-background/40">
              <div className="flex items-center gap-3">
                {canUpdate && Number(detailReceipt.dueAmount || 0) > 0 && (
                  <button
                    onClick={() => openDuePayment(detailReceiptDetail?.data ?? detailReceipt)}
                    className="px-4 py-2 rounded-lg bg-success text-white hover:bg-success/90 transition-colors text-sm font-medium flex items-center gap-2"
                    title="Record a payment against the outstanding due"
                  >
                    <Banknote size={15} />
                    Due Payment
                  </button>
                )}
                {canManageRiskFund && Number(detailReceipt.dueAmount || 0) > 0 && (
                  <button
                    onClick={() => {
                      const due = Number(detailReceipt.dueAmount) || 0;
                      setRiskFundAmount(String(due));
                      setRiskFundError(null);
                      setRiskFundConfirm({
                        mode: 'single',
                        receiptId: detailReceipt.id,
                        receiptNumber: detailReceipt.receiptNumber,
                        dueAmount: due,
                      });
                      setDetailReceipt(null);
                    }}
                    className="px-4 py-2 rounded-lg bg-warning text-white hover:bg-warning/90 transition-colors text-sm font-medium flex items-center gap-2"
                    title="Move the outstanding due to the Risk Fund"
                  >
                    <AlertTriangle size={15} />
                    Move to Risk Fund
                  </button>
                )}
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => handlePrint(detailReceipt.id)}
                  disabled={printingId === detailReceipt.id}
                  className="px-4 py-2 rounded-lg border border-border text-text hover:bg-background transition-colors text-sm font-medium flex items-center gap-2 disabled:opacity-50"
                >
                  {printingId === detailReceipt.id ? (
                    <Loader2 size={15} className="animate-spin" />
                  ) : (
                    <Printer size={15} />
                  )}
                  Print
                </button>
                <button
                  onClick={() => setDetailReceipt(null)}
                  className="px-4 py-2 rounded-lg border border-border text-text-muted hover:text-text hover:bg-background transition-colors text-sm font-medium"
                >
                  Close
                </button>
                <button
                  onClick={() => navigate(`/opd/diagnostic-receipts/${detailReceipt.id}`)}
                  className="px-4 py-2 rounded-lg bg-primary text-white hover:bg-primary-dark transition-colors text-sm font-medium flex items-center gap-2"
                >
                  <Pencil size={15} />
                  Edit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Referral calculation breakdown — opened from the "i" button beside the
          Referral line in the details modal, exactly like the edit page. */}
      {detailReceipt && showDetailReferralInfo && (
        <ReferralCalculationModal
          open={showDetailReferralInfo}
          onClose={() => setShowDetailReferralInfo(false)}
          details={{
            gross: detailBreakdown.testGross,
            percent: detailBreakdown.referralPercent,
            lines: detailBreakdown.lines,
            totalDiscount: detailBreakdown.itemDiscountAmount,
            totalShare: detailBreakdown.referralCommissionAmount,
            hospitalShare: detailBreakdown.hospitalShare,
          }}
        />
      )}

      {/* Due payment modal — opened from the receipt details modal */}
      {duePaymentReceipt && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          onClick={closeDuePayment}
        >
          <div
            className="bg-surface rounded-2xl border border-border w-full max-w-md max-h-[90vh] flex flex-col shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-start justify-between gap-4 px-6 py-5 border-b border-border bg-background/40">
              <div className="flex items-start gap-3 min-w-0">
                <div className="w-10 h-10 rounded-xl bg-success/10 flex items-center justify-center shrink-0">
                  <Banknote size={20} className="text-success" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-lg font-bold text-text leading-tight">Due Payment</h3>
                  <p className="text-sm font-mono text-text-muted mt-0.5 truncate">
                    {duePaymentReceipt.receiptNumber}
                  </p>
                </div>
              </div>
              <button
                onClick={closeDuePayment}
                className="p-1.5 rounded-lg text-text-muted hover:text-text hover:bg-background transition-colors shrink-0"
                title="Close"
              >
                <X size={18} />
              </button>
            </div>

            {/* Body */}
            <div className="overflow-y-auto px-6 py-5 space-y-4">
              {/* Balance summary */}
              <div className="rounded-xl border border-border bg-background/40 p-4 space-y-2">
                <div className="flex items-center justify-between gap-3 text-sm">
                  <span className="text-text-muted">Patient</span>
                  <span className="text-text font-medium truncate">
                    {joinFullName(duePaymentReceipt.patient || {})}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-text-muted">Gross Amount</span>
                  <span className="text-text font-medium">৳{duePaymentGross.toLocaleString()}</span>
                </div>
                {duePaymentItemDiscount > 0 && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-text-muted">Item Discounts</span>
                    <span className="text-danger font-medium">
                      -৳{duePaymentItemDiscount.toLocaleString()}
                    </span>
                  </div>
                )}
                {duePaymentExtraDiscount > 0 && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-text-muted">Extra Discount</span>
                    <span className="text-danger font-medium">
                      -৳{duePaymentExtraDiscount.toLocaleString()}
                    </span>
                  </div>
                )}
                {duePaymentReferralCommission > 0 && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-text-muted">
                      Referral commission ({duePaymentReferralPercent}%) · hospital pays
                    </span>
                    <span className="text-text-muted font-medium">
                      ৳{duePaymentReferralCommission.toLocaleString()}
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-between text-sm">
                  <span className="text-text-muted">Net Payable</span>
                  <span className="text-text font-medium">
                    ৳{duePaymentNetPayable.toLocaleString()}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-text-muted">Paid Amount</span>
                  <span className="text-success font-medium">
                    ৳{duePaymentCurrentPaid.toLocaleString()}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm pt-2 border-t border-border">
                  <span className="text-text-muted">Outstanding Due</span>
                  <span className="text-danger font-semibold">
                    ৳{duePaymentOutstanding.toLocaleString()}
                  </span>
                </div>
              </div>

              {/* Extra discount */}
              <div>
                <label className="block text-xs font-medium text-text-muted mb-1 uppercase">
                  Extra Discount
                </label>
                <div className="flex items-center gap-2">
                  <div className="inline-flex rounded-lg border border-border overflow-hidden shrink-0">
                    <button
                      type="button"
                      onClick={() => setDuePaymentDiscountType('FLAT')}
                      title="Flat amount"
                      className={`px-2.5 py-2 text-xs font-bold transition-colors ${
                        duePaymentDiscountType === 'FLAT'
                          ? 'bg-primary text-white'
                          : 'bg-surface text-text-muted hover:text-text'
                      }`}
                    >
                      ৳
                    </button>
                    <button
                      type="button"
                      onClick={() => setDuePaymentDiscountType('PERCENT')}
                      title="Percentage"
                      className={`px-2.5 py-2 text-xs font-bold border-l border-border transition-colors ${
                        duePaymentDiscountType === 'PERCENT'
                          ? 'bg-primary text-white'
                          : 'bg-surface text-text-muted hover:text-text'
                      }`}
                    >
                      %
                    </button>
                  </div>
                  <ValidatedNumberInput
                    value={duePaymentDiscountValue}
                    onChange={setDuePaymentDiscountValue}
                    max={
                      duePaymentDiscountType === 'PERCENT'
                        ? 100
                        : duePaymentMaxExtraDiscount
                    }
                    placeholder="0.00"
                    className="flex-1 px-3 py-2 rounded-lg border border-border bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                  />
                </div>
                <p className="text-[0.6875rem] text-text-muted mt-1">
                  {duePaymentDiscountType === 'PERCENT'
                    ? `Percentage of the gross total. Applies ${duePaymentExtraDiscount > 0 ? `৳${duePaymentExtraDiscount.toLocaleString()} off` : 'nothing yet'}.`
                    : `Flat amount off, up to ৳${duePaymentMaxExtraDiscount.toLocaleString()}.`}
                </p>
              </div>

              {/* Amount */}
              <div>
                <label className="block text-xs font-medium text-text-muted mb-1 uppercase">
                  Amount Received
                </label>
                <ValidatedNumberInput
                  value={duePaymentAmount}
                  onChange={(value) => {
                    setDuePaymentAmountDirty(true);
                    setDuePaymentAmount(value);
                  }}
                  max={duePaymentOutstanding}
                  placeholder="0.00"
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                />
                <div className="flex items-center justify-between mt-1.5">
                  <p className="text-[0.6875rem] text-text-muted">
                    Max ৳{duePaymentOutstanding.toLocaleString()}
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setDuePaymentAmountDirty(false);
                      setDuePaymentAmount(duePaymentOutstanding);
                    }}
                    className="text-[0.6875rem] font-medium text-primary hover:underline"
                  >
                    Pay full due
                  </button>
                </div>
              </div>

              {/* Date received — the collected amount posts on this date. */}
              <div>
                <label className="block text-xs font-medium text-text-muted mb-1 uppercase">
                  Payment Date
                </label>
                <input
                  type="datetime-local"
                  value={duePaymentDate}
                  onChange={(e) => setDuePaymentDate(e.target.value)}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
                <p className="text-[0.6875rem] text-text-muted mt-1">
                  This is the date the amount is added to collected.
                </p>
              </div>

              {/* Promised date for any balance left after this payment. */}
              {duePaymentRemaining > 0 && (
                <div>
                  <label className="block text-xs font-medium text-text-muted mb-1 uppercase">
                    Promised Due Date{' '}
                    <span className="normal-case font-normal">(optional)</span>
                  </label>
                  <input
                    type="date"
                    value={duePaymentDueDate}
                    onChange={(e) => setDuePaymentDueDate(e.target.value)}
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/50"
                  />
                  <p className="text-[0.6875rem] text-text-muted mt-1">
                    Used to flag this receipt as Overdue after this date.
                  </p>
                </div>
              )}

              {/* Payment mode */}
              <div>
                <label className="block text-xs font-medium text-text-muted mb-1 uppercase">
                  Payment Mode
                </label>
                <select
                  value={duePaymentMode}
                  onChange={(e) => setDuePaymentMode(e.target.value)}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/50"
                >
                  {PAYMENT_MODES.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Reference */}
              <div>
                <label className="block text-xs font-medium text-text-muted mb-1 uppercase">
                  Reference{' '}
                  <span className="normal-case font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  value={duePaymentReference}
                  onChange={(e) => setDuePaymentReference(e.target.value)}
                  placeholder="e.g. txn id, cheque no."
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
              </div>

              {/* Remaining due preview */}
              <div className="flex items-center justify-between text-sm pt-2 border-t border-border">
                <span className="text-text font-medium">Remaining Due</span>
                <span className="text-text font-semibold">
                  ৳{duePaymentRemaining.toLocaleString()}
                </span>
              </div>

              {duePaymentError && (
                <div className="flex items-start gap-2 p-3 bg-danger/10 border border-danger/20 rounded-lg text-sm text-danger">
                  <AlertCircle size={16} className="shrink-0 mt-0.5" />
                  <span>{duePaymentError}</span>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border bg-background/40">
              <button
                onClick={closeDuePayment}
                disabled={recordPaymentMutation.isPending}
                className="px-4 py-2 rounded-lg border border-border text-text-muted hover:text-text hover:bg-background transition-colors text-sm font-medium disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleRecordDuePayment}
                disabled={recordPaymentMutation.isPending || Number(duePaymentAmount) <= 0}
                className="px-4 py-2 rounded-lg bg-success text-white hover:bg-success/90 transition-colors text-sm font-medium flex items-center gap-2 disabled:opacity-50"
              >
                {recordPaymentMutation.isPending ? (
                  <Loader2 size={15} className="animate-spin" />
                ) : (
                  <Banknote size={15} />
                )}
                Record Payment
              </button>
            </div>
          </div>
        </div>
      )}

      {trashError && (
        <div className="flex items-start gap-2 p-3 bg-danger/10 border border-danger/20 rounded-lg text-sm text-danger">
          <AlertCircle size={16} className="shrink-0 mt-0.5" />
          <span>{trashError}</span>
        </div>
      )}
    </div>
  );
}
